/**
 * React Query hooks for the customer-facing support tickets feature.
 *
 * Endpoints:
 *   GET    /api/support/tickets
 *   POST   /api/support/tickets
 *   GET    /api/support/tickets/:id
 *   POST   /api/support/tickets/:id/messages
 *   POST   /api/support/tickets/:id/read
 *   GET    /api/support/tickets/unread-count
 *
 * Admin-side hooks (cross-user reads + replies + status updates) live in
 * features/admin/use-admin-support.ts so the customer bundle doesn't ship
 * staff-only query keys.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SupportTicketStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_on_user'
  | 'resolved'
  | 'closed'

export type SupportTicketCategory = 'bug' | 'billing' | 'feature_request' | 'how_to' | 'other'
export type SupportTicketPriority = 'low' | 'normal' | 'high' | 'urgent'

export type SupportTicket = {
  readonly id: string
  readonly user_id: string
  readonly subject: string
  readonly category: SupportTicketCategory
  readonly priority: SupportTicketPriority
  readonly status: SupportTicketStatus
  readonly user_unread_count: number
  readonly staff_unread_count: number
  readonly created_at: string
  readonly updated_at: string
  readonly last_message_at: string
}

export type SupportTicketMessage = {
  readonly id: string
  readonly ticket_id: string
  readonly sender_type: 'user' | 'staff'
  readonly sender_user_id: string
  readonly body: string
  readonly created_at: string
}

export type SupportTicketDetail = {
  readonly ticket: SupportTicket
  readonly messages: readonly SupportTicketMessage[]
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const supportKeys = {
  all: ['support'] as const,
  list: () => [...supportKeys.all, 'list'] as const,
  detail: (id: string) => [...supportKeys.all, 'detail', id] as const,
  unread: () => [...supportKeys.all, 'unread-count'] as const,
}

// Poll cadence — fast enough to feel live without burning the worker.
const LIST_POLL_MS = 30_000
const DETAIL_POLL_MS = 15_000
const UNREAD_POLL_MS = 60_000

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useSupportTickets() {
  const api = useApi()
  return useQuery({
    queryKey: supportKeys.list(),
    queryFn: async () => {
      const res = await api<{ tickets: SupportTicket[] }>('/api/support/tickets')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load tickets')
      return res.data.tickets
    },
    refetchInterval: LIST_POLL_MS,
  })
}

export function useSupportTicket(ticketId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: ticketId ? supportKeys.detail(ticketId) : ['support', 'detail', 'null'],
    queryFn: async () => {
      if (!ticketId) throw new Error('No ticket id')
      const res = await api<SupportTicketDetail>(`/api/support/tickets/${ticketId}`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load ticket')
      return res.data
    },
    enabled: Boolean(ticketId),
    refetchInterval: DETAIL_POLL_MS,
  })
}

export function useSupportUnreadCount() {
  const api = useApi()
  return useQuery({
    queryKey: supportKeys.unread(),
    queryFn: async () => {
      const res = await api<{ unread: number }>('/api/support/tickets/unread-count')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.unread
    },
    refetchInterval: UNREAD_POLL_MS,
  })
}

type CreateTicketInput = {
  readonly subject: string
  readonly body: string
  readonly category?: SupportTicketCategory
  readonly priority?: SupportTicketPriority
}

export function useCreateTicket() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateTicketInput): Promise<SupportTicket> => {
      const res = await api<{ ticket: SupportTicket }>('/api/support/tickets', {
        method: 'POST',
        body: {
          subject: input.subject,
          body: input.body,
          category: input.category ?? 'other',
          priority: input.priority ?? 'normal',
        },
      })
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to create ticket')
      return res.data.ticket
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.list() })
      void queryClient.invalidateQueries({ queryKey: supportKeys.unread() })
    },
  })
}

type ReplyInput = {
  readonly ticketId: string
  readonly body: string
}

export function useReplyToTicket() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ReplyInput): Promise<SupportTicketMessage> => {
      const res = await api<{ message: SupportTicketMessage }>(
        `/api/support/tickets/${input.ticketId}/messages`,
        { method: 'POST', body: { body: input.body } },
      )
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to send reply')
      return res.data.message
    },
    onSuccess: (_msg, vars) => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.detail(vars.ticketId) })
      void queryClient.invalidateQueries({ queryKey: supportKeys.list() })
    },
  })
}

export function useMarkTicketRead() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ticketId: string): Promise<void> => {
      const res = await api<unknown>(`/api/support/tickets/${ticketId}/read`, {
        method: 'POST',
      })
      if (!res.success) throw new Error(res.error ?? 'Failed')
    },
    onSuccess: (_v, ticketId) => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.list() })
      void queryClient.invalidateQueries({ queryKey: supportKeys.detail(ticketId) })
      void queryClient.invalidateQueries({ queryKey: supportKeys.unread() })
    },
  })
}

// ---------------------------------------------------------------------------
// Pure helpers — used by both customer and admin UIs.
// ---------------------------------------------------------------------------

export function statusLabel(status: SupportTicketStatus): string {
  switch (status) {
    case 'open': return 'Open'
    case 'in_progress': return 'In progress'
    case 'waiting_on_user': return 'Waiting on you'
    case 'resolved': return 'Resolved'
    case 'closed': return 'Closed'
  }
}

export function categoryLabel(c: SupportTicketCategory): string {
  switch (c) {
    case 'bug': return 'Bug'
    case 'billing': return 'Billing'
    case 'feature_request': return 'Feature request'
    case 'how_to': return 'How-to'
    case 'other': return 'Other'
  }
}

export function priorityLabel(p: SupportTicketPriority): string {
  switch (p) {
    case 'low': return 'Low'
    case 'normal': return 'Normal'
    case 'high': return 'High'
    case 'urgent': return 'Urgent'
  }
}
